import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date");

const resolveSchema = z.object({
  initData: z.string().max(4096).optional(),
  fullName: z.string().trim().min(1).max(80).optional(),
  deviceId: z.string().trim().min(6).max(64).optional(),
});

export const resolveSession = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => resolveSchema.parse(input))
  .handler(async ({ data }) => {
    const s = await import("./session.server");

    if (data.initData) {
      const tgUser = s.verifyTelegramInitData(data.initData);
      if (tgUser) {
        const profile = await s.upsertTelegramProfile(tgUser);
        s.writeSessionCookie(profile.id);
        return { status: "ok" as const, profile };
      }
    }

    const existingId = s.readSessionCookie();

    // Signed in with Supabase Auth (Google or anonymous guest).
    const authUser = await s.readAuthUser();
    if (authUser) {
      const profile = await s.resolveAuthProfile(authUser, existingId);
      s.writeSessionCookie(profile.id);
      return { status: "ok" as const, profile };
    }

    if (existingId) {
      const profile = await s.loadProfile(existingId);
      if (profile) {
        await s.touchProfile(profile.id);
        s.writeSessionCookie(profile.id);
        return { status: "ok" as const, profile };
      }
    }

    if (data.fullName && data.deviceId) {
      const profile = await s.createGuestProfile(data.fullName, data.deviceId);
      s.writeSessionCookie(profile.id);
      return { status: "ok" as const, profile };
    }

    return { status: "needs_name" as const, profile: null };
  });

export const fetchDashboard = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => z.object({ today: dateSchema }).parse(input))
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { getDashboard } = await import("./study.server");
    const profile = await requireProfile();
    return getDashboard(profile.id, data.today);
  });

export const endSession = createServerFn({ method: "POST" }).handler(async () => {
  const { clearSessionCookie } = await import("./session.server");
  clearSessionCookie();
  return { ok: true };
});

export const saveDailyGoal = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ today: dateSchema, minutes: z.number().int().min(15).max(1440) }).parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { setDailyGoal } = await import("./study.server");
    const profile = await requireProfile();
    await setDailyGoal(profile.id, data.today, data.minutes);
    return { ok: true };
  });

export const saveStudySession = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        subject: z.string().trim().min(1).max(40),
        chapter: z.string().trim().min(1).max(120),
        durationSeconds: z.number().int().min(1).max(86400),
        startedAt: z.string(),
        endedAt: z.string(),
        today: dateSchema,
        chapterCompleted: z.boolean(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { finishSession } = await import("./study.server");
    const profile = await requireProfile();
    return finishSession(profile.id, data);
  });

export const fetchRevisions = createServerFn({ method: "POST" }).handler(async () => {
  const { requireProfile } = await import("./session.server");
  const { listRevisions } = await import("./study.server");
  const profile = await requireProfile();
  return listRevisions(profile.id);
});

export const completeRevisionTask = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        taskId: z.string().uuid(),
        today: dateSchema,
        quality: z.enum(["forgot", "average", "good", "perfect"]),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { completeRevision } = await import("./study.server");
    const profile = await requireProfile();
    await completeRevision(profile.id, data.taskId, data.today, data.quality);
    return { ok: true };
  });

export const rescheduleRevisionTask = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        taskId: z.string().uuid(),
        dueDate: dateSchema,
        action: z.enum(["snoozed", "rescheduled"]),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { requireProfile } = await import("./session.server");
    const { rescheduleRevision } = await import("./study.server");
    const profile = await requireProfile();
    await rescheduleRevision(profile.id, data.taskId, data.dueDate, data.action);
    return { ok: true };
  });